import { ApolloLink, Operation, FetchResult, NextLink, Observable } from '@apollo/client/core';
import { AuthInitializerService } from './auth-initializer.service';
import { AuthService } from '@auth0/auth0-angular';
import { Router } from '@angular/router';

/**
 * Apollo Link that ensures authentication is complete before allowing GraphQL operations to proceed
 */
export class AuthWaitLink extends ApolloLink {
  private maxRetries = 2;
  
  constructor(
    private authInitializer: AuthInitializerService,
    public router: Router,
    private authService: AuthService
  ) {
    super();
  }

  request(operation: Operation, forward?: NextLink): Observable<FetchResult> {
    if (!forward) {
      throw new Error('AuthWaitLink requires a NextLink');
    }

    
    // Create an observable that waits for authentication to be ready before proceeding
    return new Observable<FetchResult>(observer => {
      // Attempt the operation with retries
      this.attemptOperation(operation, forward, observer, 0);
      
      // Return a no-op cleanup function
      return () => {};
    });
  }
  
  private attemptOperation(operation: Operation, forward: NextLink, observer: any, retryCount: number) {
    // Wait for auth to be ready
    this.authInitializer.waitForAuth().then(
      async () => {
        try {
          // Force a token refresh before proceeding
          if (retryCount > 0) {
            await this.authInitializer.forceTokenRefresh();
          }
          
          // Set once this attempt hands the operation to a retry (or login), so
          // its own trailing complete() doesn't close the observer early.
          let superseded = false;

          const recoverFromTokenError = (message: string, propagate: () => void) => {
            console.error(`AuthWaitLink: Token error for ${operation.operationName}: ${message}`);

            // If we haven't exceeded max retries, try again after forcing a login
            if (retryCount < this.maxRetries) {
              superseded = true;
              // Force a login redirect with the current URL as the target
              if (retryCount === this.maxRetries - 1) {
                this.authInitializer.login();
                return;
              }

              // Wait a moment and retry
              setTimeout(() => {
                this.attemptOperation(operation, forward, observer, retryCount + 1);
              }, 1000);
            } else {
              // We've exceeded max retries, propagate the error
              propagate();
            }
          };

          // Once auth is ready, proceed with the operation
          forward(operation).subscribe({
            next: result => {
              // Hasura can answer an expired/invalid JWT with HTTP 200 and
              // `errors[].extensions.code:
              // invalid-jwt`, which httpLink emits as a normal result rather
              // than an error. Without this, those results reached components
              // as `data: undefined` with no refresh attempted
              // (SLAPFIVE-FRONTEND-A64 / #16968).
              if (result.errors?.length && this.isTokenError({ graphQLErrors: result.errors })) {
                recoverFromTokenError(result.errors[0].message, () => observer.next(result));
                return;
              }
              observer.next(result);
            },
            error: err => {
              // Check if this is a token-related error that can be recovered by refreshing
              if (this.isTokenError(err)) {
                recoverFromTokenError(err.message, () => observer.error(err));
              } else {
                // For other errors, just propagate them
                observer.error(err);
              }
            },
            complete: () => {
              if (!superseded) {
                observer.complete();
              }
            }
          });
        } catch (error) {
          console.error('AuthWaitLink: Error during operation execution', error);
          observer.error(error);
        }
      },
      error => {
        // If a redirect is in progress, don't retry or propagate - the page will navigate away
        if (error?.message === 'REDIRECT_IN_PROGRESS') {
          return;
        }

        console.error('AuthWaitLink: Error waiting for auth', error);

        // If auth failed and we haven't exceeded max retries, try again
        if (retryCount < this.maxRetries) {
          setTimeout(() => {
            this.attemptOperation(operation, forward, observer, retryCount + 1);
          }, 1000);
        } else {
          observer.error(error);
        }
      }
    );
  }

  /**
   * Detect JWT/refresh-token failures regardless of how Apollo surfaces them.
   *
   * Hasura returns invalid/expired JWTs as a non-2xx HTTP response. Apollo's
   * httpLink converts that into a generic `ServerError` whose top-level
   * `.message` is just "Response not successful: Received status code 400" —
   * the real `"Could not verify JWT: JWTExpired"` message and
   * `extensions.code: "invalid-jwt"` only live in `err.result.errors[]`.
   * Checking `err.message` alone (as before) never matches that shape, so
   * this also inspects the embedded GraphQL error payload before giving up
   * on the refresh-and-retry path below.
   */
  private isTokenError(err: any): boolean {
    if (!err) {
      return false;
    }

    const candidates: string[] = [];
    if (typeof err.message === 'string') {
      candidates.push(err.message);
    }

    const embeddedErrors: any[] = err.result?.errors || err.graphQLErrors || [];
    for (const gqlError of embeddedErrors) {
      if (typeof gqlError?.message === 'string') {
        candidates.push(gqlError.message);
      }
      if (typeof gqlError?.extensions?.code === 'string') {
        candidates.push(gqlError.extensions.code);
      }
    }

    return candidates.some(text =>
      text.includes('Missing Refresh Token') ||
      text.includes('JWTExpired') ||
      text.includes('invalid-jwt') ||
      text.includes('Could not verify JWT')
    );
  }
}
